import React, { useState, useEffect, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styles from "./SideBar.module.css";
import { LuCalendarDays, LuUsers, LuLayoutGrid, LuLogOut, LuCog} from 'react-icons/lu'
import { IoIosArrowDropleftCircle, IoIosArrowDroprightCircle } from "react-icons/io";
import { FaQuestion } from 'react-icons/fa6';
import { NotificationButton } from "../NotificationButton/NotificationButton";
import { HelpModal } from "../HelpModal/HelpModal";
import { ConfirmationModal } from "../ConfirmationModal/ConfirmationModal.js"
import AuthContext from "../../context/authProvider";

const SideBar = ({ onPlayerRefresh }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // state for logout confirmation
  const { setAuth } = useContext(AuthContext);
  const navigate = useNavigate(); // used to log user out *** IN FUTURE CALL ENDPOINT TO LOG USER OUT

  // Top nav buttons
  const topNavItems = [
    //{ icon: <LuLayoutGrid />, text: "Dashboard", link: "/dashboard" },
    { icon: <LuUsers />, text: "Recruits", link: "/recruits" },
    { icon: <LuCalendarDays />, text: "Calendar", link: "/calendar" },
  ];

  // Setting/signout bottom nav buttons
  const bottomNavItems = [
    { icon: <FaQuestion />, text: "Help", onClick: () => setShowHelp(true) },
    { icon: <LuCog />, text: "Settings", link: "/profile" },
    { icon: <LuLogOut />, text: "Logout", link: "/", onClick: (e) => {
      e.preventDefault()
      setIsLoggingOut(true)}
    },
  ];

  // Automatically collapse when window gets to small
  useEffect(() => {
    const autoCollapse = () => {
      if (window.innerWidth < 1000) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    // Run immediately, and watch for any window resizing
    autoCollapse();
    window.addEventListener("resize", autoCollapse);
    // Remove listenting to avoid memory leaks when sidebar not loaded
    return () => window.removeEventListener("resize", autoCollapse);
  }, []);

  // Helper function to get and set styling for active nav item
  const getLinkClassName = (isActive) => {
    return isActive
      ? `${styles.navItem} ${styles.activeNavItem}`
      : styles.navItem;
  };

  return (
    <>
      <nav className={isCollapsed ? styles.collapsedSideBar : styles.sideBar}>
        <div className={styles.contents}>
          <header className={styles.top}>
            <img
              src="/icons/TTLogo.png"
              alt="Talent Track Logo"
              className={styles.logoImg}
            />
            {!isCollapsed && <h1 className={styles.brandName}>Talent Track</h1>}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={styles.collapseIcon}
            >
              {isCollapsed ? (
                <IoIosArrowDroprightCircle />
              ) : (
                <IoIosArrowDropleftCircle />
              )}
            </button>
          </header>

          <div className={styles.navItems}>
            {/* Top Navigation Items*/}
            <div className={styles.divider}/>
              <NotificationButton isCollapsed={isCollapsed} onPlayerRefresh={onPlayerRefresh} />
            <div className={styles.divider}/>
              
            <div className={styles.topNavItems}>
              {topNavItems.map((item, index) => (
                item.link ? (       // Find if the navigation item is a button or nav link
                  <NavLink 
                    key={index}
                    to={item.link}
                    className={({ isActive }) => getLinkClassName(isActive)}
                  >
                    <span className={styles.navIcon}>{item.icon}</span>
                    {!isCollapsed && (
                      <span className={styles.navText}>{item.text}</span>
                    )}
                  </NavLink>
                ) : (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className={styles.navItem}
                  >
                    <span className={styles.navIcon}>{item.icon}</span>
                    {!isCollapsed && (
                      <span className={styles.navText}>{item.text}</span>
                    )}
                  </button>
                )
              ))}
            </div>

            <div className={styles.bottomNavItems}>
              {bottomNavItems.map((item, index) => (
                item.link ? (       // Find if the navigation item is a button or nav link
                  <NavLink 
                    key={index}
                    to={item.link}
                    onClick={(e) => {
                      if (item.onClick) {
                        item.onClick(e); // optional onClick logic handling for logout
                      }
                    }}
                    className={({ isActive }) => getLinkClassName(isActive)}
                  >
                    <span className={styles.navIcon}>{item.icon}</span>
                    {!isCollapsed && (
                      <span className={styles.navText}>{item.text}</span>
                    )}
                  </NavLink>
                ) : (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className={styles.navItem}
                  >
                    <span className={styles.navIcon}>{item.icon}</span>
                    {!isCollapsed && (
                      <span className={styles.navText}>{item.text}</span>
                    )}
                  </button>
                )
              ))}
            </div>
          </div>
        </div>
      </nav>
       
      {isLoggingOut && (
        <ConfirmationModal
          message="Are you sure you want to log out?"
          onConfirm={() => {
            localStorage.removeItem('jwtToken'); // Clears all browser storage (including jwtToken) to ensure complete logout 
            setAuth({}) // Clear auth context
            navigate('/'); // Navigate to landingpage
          }}
          onCancel={() => setIsLoggingOut(false)}
        />
      )}
  
      <HelpModal showHelp={showHelp} setShowHelp={setShowHelp} />
    </>
  );
};

export default SideBar;
